<template>
  <div
    v-if="field.type === FieldType.DATE || field.type == FieldType.DATETIME"
    class="d-flex align-items-center flex-wrap date-values"
  >
    <date-select
      class="date-value"
      :data-text-between-pills="isInverted ? 'AND' : 'OR'"
      v-for="(val, index) in value"
      :key="index"
      :value="val"
      @change="onDateChange(index, $event)"
      @remove="onDateChange(index)"
    />
    <date-select
      class="date-value"
      @change="onDateChange(value.length, $event)"
      v-if="cardinality === CARDINALITY.MANY || (cardinality === CARDINALITY.ONE && !value[0])"
    />
  </div>
  <div v-else-if="cardinality === CARDINALITY.MANY">
    <multi-select
      :options="fieldOptions"
      :values="selectedValues"
      :loading="isLoading"
      :text-between-pills="isInverted ? 'AND' : 'OR'"
      :hide-border="true"
      menu-alignment="right"
      :menu-width="field.id === InitiativeFieldId.KEY_RESULT ? 300 : 220"
      @change="onMultiSelectChange"
      placeholder="Choose"
    >
      <template
        v-slot:default="item"
        v-if="field.id === InitiativeFieldId.OWNER || field.type === CUSTOM_FIELD_TYPE.USER"
      >
        <user-pill-template :item="item.value" />
      </template>
      <template v-slot:default="item" v-else-if="field.id === InitiativeFieldId.STATUS">
        <status-pill-template :item="item.value" />
      </template>
      <template v-slot:default="item" v-else-if="field.id === InitiativeFieldId.KEY_RESULT">
        <kr-pill-template :item="item.value" />
      </template>
      <template v-slot:default="item" v-else-if="field.id === InitiativeFieldId.PROJECT">
        <project-pill-template :item="item.value" />
      </template>
      <template
        v-slot:listItem="option"
        v-if="field.id === InitiativeFieldId.OWNER || field.type === CUSTOM_FIELD_TYPE.USER"
      >
        <user-list-item-template :option="option.option" />
      </template>
      <template v-slot:listItem="option" v-else-if="field.id === InitiativeFieldId.STATUS">
        <status-list-item-template :option="option.option" />
      </template>
      <template v-slot:listItem="option" v-else-if="field.id === InitiativeFieldId.KEY_RESULT">
        <kr-list-item-template :option="option.option" />
      </template>
      <template v-slot:listItem="option" v-else-if="field.id === InitiativeFieldId.PROJECT">
        <project-list-item-template :option="option.option" />
      </template>
    </multi-select>
  </div>
  <div v-else-if="cardinality === CARDINALITY.ONE">
    <input
      :value="value[0]"
      @keyup="lastKey = $event.keyCode"
      @keyup.enter="onInputEnter"
      @blur="lastKey !== KEY_CODES.ESCAPE && onInputBlur($event)"
      class="input-field"
      autocomplete="off"
      :type="field.type === CUSTOM_FIELD_TYPE.NUMBER ? 'number' : 'text'"
      placeholder="Type and hit enter"
    />
  </div>
</template>

<script>
import DateSelect from '@/components/conditionSelector/DateSelect'
import KrListItemTemplate from '@/components/conditionSelector/KrListItemTemplate'
import KrPillTemplate from '@/components/conditionSelector/KrPillTemplate'
import ProjectListItemTemplate from '@/components/conditionSelector/ProjectListItemTemplate.vue'
import ProjectPillTemplate from '@/components/conditionSelector/ProjectPillTemplate.vue'
import StatusListItemTemplate from '@/components/conditionSelector/StatusListItemTemplate'
import StatusPillTemplate from '@/components/conditionSelector/StatusPillTemplate'
import UserListItemTemplate from '@/components/conditionSelector/UserListItemTemplate'
import UserPillTemplate from '@/components/conditionSelector/UserPillTemplate'
import MultiSelect from '@/components/form/MultiSelect'
import { CUSTOM_FIELD_TYPE } from '@/constants/customFields'
import {
  CARDINALITY,
  FIELD_OPTIONS_MAP,
  FieldType,
  InitiativeFieldId,
  Operator
} from '@/constants/initiative/filter'
import { KEY_CODES } from '@/constants/keyCodes'
import { intervalWithId } from '@/interval'
import ComponentIdMixin from '@/mixins/ComponentIdMixin'
import getMembersList from '@/utils/filters/getMembersList'
import getIdFromCFPrefixedField from '@/utils/initiative/getIdFromCFPrefixedField'
import getOperatorCardinality from '@/utils/initiative/getOperatorCardinality'
import showAPIError from '@/utils/showAPIError'
import * as R from 'ramda'
import { mapActions } from 'vuex'

/**
 * This component provides capability to select value for a filter expression
 */
export default {
  name: 'ConditionSelectorValueField',
  props: ['value', 'field', 'operator', 'orgId', 'timeline', 'filterConfiguration'],
  mixins: [ComponentIdMixin],
  components: {
    ProjectPillTemplate,
    ProjectListItemTemplate,
    MultiSelect,
    UserPillTemplate,
    UserListItemTemplate,
    StatusPillTemplate,
    StatusListItemTemplate,
    KrPillTemplate,
    KrListItemTemplate,
    DateSelect
  },
  data() {
    return {
      FieldType,
      Operator,
      KEY_CODES,
      lastKey: null,
      isLoading: false,
      options: [],
      InitiativeFieldId,
      CUSTOM_FIELD_TYPE,
      CARDINALITY
    }
  },
  computed: {
    isInverted() {
      return this.operator.startsWith('not-')
    },
    fieldOptions() {
      if (this.field.fieldType === 'custom_field' && this.field.type === FieldType.DROPDOWN) {
        return this.getCustomFieldDropdownOptions().filter(field => !this.value.includes(field.id))
      } else if (this.field.type === FieldType.USER) {
        return this.getMemberShips().filter(user => !this.value.includes(user.id))
      }
      const options = FIELD_OPTIONS_MAP[this.field.id] || this.options
      return options.filter(option => !this.value.includes(option.id))
    },
    selectedValues() {
      if (this.field.fieldType === 'custom_field' && this.field.type === FieldType.DROPDOWN) {
        return this.value
          .map(val => this.getCustomFieldDropdownOptions().find(option => option.id === val))
          .filter(Boolean)
      } else if (this.field.type === FieldType.USER) {
        return this.value
          .map(val => this.getMemberShips().find(option => option.id === val))
          .filter(Boolean)
      }
      const options = FIELD_OPTIONS_MAP[this.field.id] || this.options
      return this.value.map(val => options.find(option => option.id === val)).filter(Boolean)
    },
    cardinality() {
      return getOperatorCardinality(this.filterConfiguration, this.operator)
    },
    currentUserId() {
      return this.$store.getters.user.id
    }
  },
  methods: {
    ...mapActions(['getGoals']),
    ...mapActions('project', ['getProjects']),
    getCustomFieldDropdownOptions() {
      const cfs = this.$store.state.orgCustomFields[this.orgId]
      const cfDefinition = cfs.find(x => x.id === getIdFromCFPrefixedField(this.field.id)) // field id looks like cf_<cf_id>
      return (
        cfDefinition?.dropdownOptions?.map(option => ({
          ...option,
          label: option.name
        })) || []
      )
    },
    getMemberShips() {
      return getMembersList(this.$store.getters.orgMemberships(this.orgId), this.currentUserId)
    },
    onDateChange(index, value) {
      let newValue = [...this.value]
      if (!value) {
        newValue = R.remove(index, 1, newValue)
      } else {
        newValue[index] = value
      }
      this.$emit('change', newValue)
    },
    onMultiSelectChange(values) {
      this.$emit('change', values.map(val => val.id))
    },
    onInputBlur(event) {
      this.$emit('change', event.target.value)
    },
    onInputEnter(event) {
      event.target.blur()
      this.$emit('change', event.target.value)
    },
    async fetchKRs() {
      const options = {
        isArchived: false
      }
      if (this.timeline && this.timeline !== 'any') {
        const { start, end, subset } = intervalWithId(this.timeline)
        options.intervalStart = start
        options.intervalEnd = end
        options.intervalSubset = subset
      }
      try {
        this.isLoading = true
        const goals = await this.getGoals({
          orgId: this.orgId,
          options
        })
        this.options = goals
          .map(goal => goal.keyResults)
          .flat()
          .map(kr => ({
            ...kr,
            label: kr.title
          }))
        this.isLoading = false
      } catch (e) {
        showAPIError(e, undefined, 'Error while fetching goals')
      }
    },
    async fetchProjects() {
      try {
        this.isLoading = true
        const projects = await this.getProjects({ orgId: this.orgId })
        this.options = projects.map(project => ({
          ...project,
          label: project.title
        }))
        this.isLoading = false
      } catch (e) {
        showAPIError(e, undefined, 'Error while fetching projects')
      }
    }
  },
  mounted() {
    if (this.field.type === InitiativeFieldId.KEY_RESULT) {
      this.fetchKRs()
    } else if (this.field.type === InitiativeFieldId.PROJECT) {
      this.fetchProjects()
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~@/styles/_styleConfigurations.scss';

.input-field {
  border: none;
  background-color: $quaternary;
  border-radius: 10px;
  padding-left: 10px;

  &:focus {
  }
}

$gap: 8px;

.date-values {
  gap: $gap;
}

.date-value {
  display: flex;

  &:not(:last-child):after {
    content: attr(data-text-between-pills);
    margin-left: $gap;
    color: $secondary;
    align-self: center;
  }
}
</style>
